/**
 * Wizard helpers — step ordering, prefill mapping, parameter defaults.
 *
 * The 4-step wizard is the canonical **create-only** flow for both `nabídka`
 * and `smlouva` (AGENTS.md Editing patterns): once an id is allocated by step
 * 1 (zákazník), steps 2–4 mutate the same in-flow record, and after that all
 * editing lives on the detail pages (`LeasingEditModals.tsx` section dialogs +
 * the standalone kalkulace pages) — nothing deep-links back into the wizard.
 * `parametersFromRecords` / `DEFAULT_PARAMETERS` also seed the edit modals'
 * full-body parameters draft (see `section-forms.tsx`). Step ordering and
 * labels mirror `.agents/design/project/artboard-radical-leasing-wizard.jsx:328-333`.
 */

import { coerceInt, isStepId } from '@/lib/wizard-search';
import type {
  LeasingKind,
  ParametersStepBody,
  VehicleStepBody,
} from './api';
import { readNum, readStr } from './helpers';

export type WizardStepId = 'zakaznik' | 'vozidlo' | 'parametry' | 'kalkulace';

export const STEP_ORDER: WizardStepId[] = [
  'zakaznik',
  'vozidlo',
  'parametry',
  'kalkulace',
];

export const STEP_TITLES: Record<WizardStepId, string> = {
  zakaznik: 'Zákazník',
  vozidlo: 'Vozidlo',
  parametry: 'Parametry',
  kalkulace: 'Kalkulace',
};

export function nextStep(step: WizardStepId): WizardStepId | null {
  const i = STEP_ORDER.indexOf(step);
  if (i < 0 || i === STEP_ORDER.length - 1) return null;
  return STEP_ORDER[i + 1];
}

export function prevStep(step: WizardStepId): WizardStepId | null {
  const i = STEP_ORDER.indexOf(step);
  if (i <= 0) return null;
  return STEP_ORDER[i - 1];
}

export function isWizardStep(s: unknown): s is WizardStepId {
  return isStepId(STEP_ORDER, s);
}

export function isLeasingKind(s: unknown): s is LeasingKind {
  return s === 'nabidka' || s === 'smlouva';
}

export interface LeasingWizardSearch {
  kind?: LeasingKind;
  id?: number;
  step?: WizardStepId;
  /** Round-trip param from the "Nový zákazník" form (`?id_zakaznika=N`). */
  id_zakaznika?: number;
}

export function parseWizardSearch(input: Record<string, unknown>): LeasingWizardSearch {
  const out: LeasingWizardSearch = {};
  const k = input.kind;
  if (isLeasingKind(k)) out.kind = k;
  const id = coerceInt(input.id);
  if (id != null) out.id = id;
  const s = input.step;
  if (isWizardStep(s)) out.step = s;
  const z = coerceInt(input.id_zakaznika);
  if (z != null) out.id_zakaznika = z;
  return out;
}

// ==================== Defaults / prefill ====================

export const DEFAULT_PARAMETERS: ParametersStepBody = {
  delka: 12,
  km_rok: 10000,
  kauce_pocatek: 0,
  kauce_konec: 0,
  kauce_rozpustit: 0,
  havarijni: 0,
  povinne: 0,
  servis: 0,
  pneu: 0,
  radio: 0,
  dalnice: 0,
  skla: 0,
  nahradni_auto: 0,
  gps: 0,
  zustatkova_cena: 0,
  letni_pneu: '',
  zimni_pneu: '',
  hav_spolu: '',
  pov_limit: '',
  km_do: '',
  km_pres: '',
  tolerance: '',
};

/**
 * Project the smlouva (or nabidka) record into the vehicle-step form shape.
 */
export function vehicleFromRecord(
  rec: Record<string, unknown> | null | undefined,
): VehicleStepBody {
  return {
    typ_vozu: readStr(rec, 'typ_vozu'),
    provedeni: readStr(rec, 'provedeni'),
    verze: readStr(rec, 'verze'),
    barva: readStr(rec, 'barva'),
    motor: readStr(rec, 'motor'),
    prevodovka: readStr(rec, 'prevodovka'),
    dph: readStr(rec, 'dph') || 'Ano',
    zakladni_cena: readNum(rec, 'zakladni_cena'),
    sleva_zakladni_cena: readNum(rec, 'sleva_zakladni_cena'),
  };
}

export function parametersFromRecords(
  rec: Record<string, unknown> | null | undefined,
  parametry: Record<string, unknown> | null | undefined,
): ParametersStepBody {
  return {
    delka: readNum(rec, 'delka') || 12,
    km_rok: readNum(rec, 'km_rok'),
    kauce_pocatek: readNum(rec, 'kauce_pocatek'),
    kauce_konec: readNum(rec, 'kauce_konec'),
    kauce_rozpustit: readNum(rec, 'kauce_rozpustit'),
    havarijni: readNum(rec, 'havarijni'),
    povinne: readNum(rec, 'povinne'),
    servis: readNum(rec, 'servis'),
    pneu: readNum(rec, 'pneu'),
    radio: readNum(rec, 'radio'),
    dalnice: readNum(rec, 'dalnice'),
    skla: readNum(rec, 'skla'),
    nahradni_auto: readNum(rec, 'nahradni_auto'),
    gps: readNum(rec, 'gps'),
    zustatkova_cena: readNum(rec, 'zustatkova_cena'),
    letni_pneu: readStr(parametry, 'letni_pneu'),
    zimni_pneu: readStr(parametry, 'zimni_pneu'),
    hav_spolu: readStr(parametry, 'hav_spolu'),
    pov_limit: readStr(parametry, 'pov_limit'),
    km_do: readStr(parametry, 'km_do'),
    km_pres: readStr(parametry, 'km_pres'),
    tolerance: readStr(parametry, 'tolerance'),
  };
}

export function dphLabel(dph: string): string {
  if (dph === 'Ano') return 'Ceny vč. DPH';
  if (dph === 'Ne') return 'Ceny bez DPH';
  return 'Osvobozeno od DPH';
}

export function dphInfoSentence(dph: string): string {
  if (dph === 'Ano') return 'Všechny ceny jsou včetně DPH.';
  if (dph === 'Ne') return 'Všechny ceny jsou bez DPH.';
  return 'Osvobozeno od DPH.';
}
